import { Element, useEditor, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Speaker } from "src/types";
import { useBetween } from "use-between";

import PrimitivesFloatingOptions from "@/components/editor/block-outline/floating-options/primitives-floating-component";
import Text from "@/components/editor/primitives/text/text-component";
import { SpeakerDetails } from "@/components/editor/shared/speakers/speaker-bio/speaker-details-component";
import {
  ActionText,
  ZoomCard,
} from "@/components/editor/shared/speakers/speaker-bio/speaker-details-styles";
import { SpeakerModal } from "@/components/editor/shared/speakers/speaker-bio/speaker-modal";
import SpeakerCardSettings from "@/components/editor/shared/speakers/speaker-card-settings/speaker-card-settings-component";
import {
  getSocialLinkAriaLabel,
  textPropByNodeId,
  useSpeakerModal,
} from "@/components/editor/shared/speakers/utils";
import useTheme from "@/components/editor/themes/use-theme";
import {
  ColorLabels,
  getColorByLabel,
} from "@/components/editor/utils/use-color";
import { LinkedInSquare } from "@/components/icons/linkedin-square";
import { Twitter } from "@/components/icons/twitter";
import { Website } from "@/components/icons/website";
import useAnalytics from "@/hooks/use-analytics";
import useProperties from "@/hooks/use-properties";
import { isAttendeesPage } from "@/utils/is-attendees-page";

import type { SpeakerLayout } from "../speakers-component";
import { Card, IconLinkWrapper } from "./speaker-card-component-styles";

export type SpeakerCardProps = {
  speaker: Speaker;
  layout?: SpeakerLayout;
  isBioAvailable?: boolean;
};

const SPEAKER_NAME_ID = "speaker-name";
const SPEAKER_HEADLINE_ID = "speaker-headline";

export default function SpeakerCard({
  speaker,
  layout = "large-cards",
  isBioAvailable,
}: SpeakerCardProps) {
  const {
    connectors: { connect },
    linkedNodes,
  } = useNode((node) => ({
    linkedNodes: node.getLinkedNodes(),
    nodeId: node.id,
  }));
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const { speaker: activeSpeaker, setSpeaker } = useBetween(useSpeakerModal);
  const { logAnalytics } = useAnalytics();
  const { enabled } = useEditor((state) => ({
    enabled: state.isEnabled(),
  }));
  const backgroundColor = getColorByLabel(ColorLabels.BACKGROUND);
  const visualsColor = getColorByLabel(ColorLabels.VISUALS);
  const speakerName = `${speaker.first_name} ${speaker.last_name}`;

  const style = React.useMemo(() => {
    if (layout === "large-cards") {
      return getLargeCardStyles(backgroundColor, visualsColor);
    }
  }, [layout, backgroundColor, visualsColor]);

  const isBioAccessible = React.useMemo(
    () => isBioAvailable && (isAttendeesPage() || !enabled),
    [enabled, isBioAvailable]
  );

  const handleClick = () => {
    if (isAttendeesPage()) {
      logAnalytics("CANVAS_SPEAKER_CARD_CLICKED", {
        is_bio_enabled: isBioAvailable,
      });
    }

    if (!isBioAccessible) {
      return null;
    }

    setSpeaker(speaker);
  };

  const properties = useProperties();

  const iconStyle = React.useMemo(() => {
    return {
      color: properties.colors[ColorLabels.PRIMARY],
    };
  }, [properties.colors]);

  return (
    <div style={style.containerStyle} data-testid="speaker-card" ref={connect}>
      {layout === "large-cards" && (
        <Card>
          <ZoomCard
            className={currentTheme.kind}
            actionable={isBioAccessible}
            onClick={handleClick}
          >
            <img
              data-testid="speaker-profile-picture"
              draggable="false"
              src={speaker.image_url}
              alt={speaker.name}
              style={style.imageStyle}
            />
          </ZoomCard>
          <ActionText
            className={currentTheme.kind}
            actionable={isBioAccessible}
            onClick={handleClick}
          >
            <Element
              id={SPEAKER_NAME_ID}
              is={Text}
              text={speaker.name}
              element="h4"
              pattern="headingFour"
              data-testid="speaker-name-field"
            />
          </ActionText>
          <Element
            id={SPEAKER_HEADLINE_ID}
            is={Text}
            text={speaker.headline}
            pattern="description"
            data-testid="speaker-headline-field"
          />
          <div style={style.socialLinks}>
            {speaker.social?.linkedin_url && (
              <IconLinkWrapper
                href={speaker.social.linkedin_url}
                target="_blank"
                rel="noreferrer"
                aria-label={getSocialLinkAriaLabel(speakerName, "LinkedIn", t)}
              >
                <LinkedInSquare
                  className="size-[18px]"
                  style={iconStyle}
                  aria-label="LinkedIn"
                />
              </IconLinkWrapper>
            )}
            {speaker.social?.twitter_url && (
              <IconLinkWrapper
                href={speaker.social.twitter_url}
                target="_blank"
                rel="noreferrer"
                aria-label={getSocialLinkAriaLabel(speakerName, "Twitter", t)}
              >
                <Twitter
                  className="size-[18px]"
                  style={iconStyle}
                  aria-label="Twitter"
                />
              </IconLinkWrapper>
            )}
            {speaker.social?.website_url && (
              <IconLinkWrapper
                href={speaker.social.website_url}
                target="_blank"
                rel="noreferrer"
                aria-label={getSocialLinkAriaLabel(speakerName, "Website", t)}
              >
                <Website
                  className="size-[18px]"
                  style={iconStyle}
                  aria-label="Website"
                />
              </IconLinkWrapper>
            )}
          </div>
        </Card>
      )}
      <SpeakerModal
        isShowing={activeSpeaker?.id === speaker.id}
        onClose={() => setSpeaker(null)}
        isDismissible
        className={currentTheme.kind}
      >
        <SpeakerDetails
          name={textPropByNodeId(linkedNodes, SPEAKER_NAME_ID)}
          headline={textPropByNodeId(linkedNodes, SPEAKER_HEADLINE_ID)}
          bio={speaker.bio}
          social={speaker.social}
          image_url={speaker.image_url}
        />
      </SpeakerModal>
    </div>
  );
}

const getLargeCardStyles = (backgroundColor: string, visualsColor: string) => {
  return {
    containerStyle: {
      width: "100%",
      maxWidth: rem(282),
      margin: `0 ${rem(12)} ${rem(24)} ${rem(12)}`,
      border: `${rem(2)} solid ${visualsColor}`,
      backgroundColor,
      padding: rem(24),
    },
    socialLinks: {
      display: "grid",
      gridAutoFlow: "column",
      gridAutoColumns: "max-content",
      margin: "auto",
      gap: rem(16),
    },
    imageStyle: {
      display: "block",
      height: rem(128),
      width: rem(128),
      objectFit: "cover",
      borderRadius: "50%",
    } as React.CSSProperties,
  };
};

SpeakerCard.craft = {
  custom: {
    displayName: "Speaker Card",
  },
  related: {
    leftOutlineOptions: PrimitivesFloatingOptions,
    settings: SpeakerCardSettings,
  },
};
